import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AttachmentService } from './attachment.service';

/**
 * Attachment Helper Service
 * Provides utility methods for attachment handling including filename extraction, download triggering,
 * and various filename/file operations
 */
@Injectable({
  providedIn: 'root',
})
export class AttachmentHelperService {
  private readonly attachmentService = inject(AttachmentService);

  /**
   * Extracts filename from Content-Disposition header
   * Handles multiple formats: filename="name.ext", filename=name.ext, filename*=UTF-8''name.ext
   * @param contentDisposition - The Content-Disposition header value
   * @returns Extracted and decoded filename
   */
  extractFilenameFromHeader(contentDisposition: string): string {
    let fileName = 'downloaded-file';

    if (!contentDisposition) {
      return fileName;
    }

    // Try to extract filename from standard format
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
      // Decode URI component if it's encoded
      fileName = decodeURIComponent(fileName);
      return fileName;
    }

    // Alternative: Try filename* format (RFC 5987)
    const filenameStarRegex = /filename\*=UTF-8''(.+)/i;
    const starMatches = filenameStarRegex.exec(contentDisposition);
    if (starMatches != null && starMatches[1]) {
      fileName = decodeURIComponent(starMatches[1]);
      return fileName;
    }

    return fileName;
  }

  /**
   * Creates and triggers a download for a blob with the specified filename
   * Uses HTML5 download API to trigger browser download
   * @param blob - The file content as Blob
   * @param fileName - The filename for the download
   */
  triggerDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Downloads an attachment by ID and triggers browser download
   * Extracts filename from response headers if available
   * @param attachmentId - The ID of the attachment to download
   * @param onError - Optional callback for error handling
   */
  downloadAttachment(attachmentId: number, onError?: (error: any) => void): void {
    this.attachmentService.download(attachmentId).subscribe({
      next: (response) => {
        const blob = response.body;
        const contentDisposition = response.headers.get('Content-Disposition');
        const fileName = this.extractFilenameFromHeader(contentDisposition || '');
        this.triggerDownload(blob, fileName);
      },
      error: (err) => {
        console.error('[AttachmentHelper] Download failed', err);
        if (onError) {
          onError(err);
        }
      },
    });
  }

  /**
   * Uploads an attachment file to a folder
   * @param file - The file to upload
   * @param folderId - The target folder ID
   * @param userId - The user ID performing the upload
   * @param delegationId - Optional delegation ID
   * @returns Observable of the upload response
   */
  uploadAttachment(
    file: File,
    folderId: number,
    userId: number,
    delegationId: number | null = null
  ): Observable<any> {
    return this.attachmentService.upload(file, folderId, userId, delegationId);
  }

  /**
   * Replaces an existing attachment with a new file
   * @param attachmentId - The ID of the attachment to replace
   * @param file - The new file to upload
   * @param delegationId - Optional delegation ID
   * @returns Observable of the replace response
   */
  replaceAttachment(
    attachmentId: number,
    file: File,
    delegationId: number | null = null
  ): Observable<any> {
    return this.attachmentService.replace(attachmentId, file, delegationId);
  }

  /**
   * Renames an attachment
   * @param attachmentId - The ID of the attachment to rename
   * @param newName - The new name for the attachment
   * @param delegationId - Optional delegation ID
   * @returns Observable of the rename response
   */
  renameAttachment(
    attachmentId: number,
    newName: string,
    delegationId: number | null = null
  ): Observable<any> {
    return this.attachmentService.rename(attachmentId, newName, delegationId);
  }

  /**
   * Deletes an attachment
   * @param attachmentId - The ID of the attachment to delete
   * @returns Observable of the delete response
   */
  deleteAttachment(attachmentId: number): Observable<any> {
    return this.attachmentService.delete(attachmentId);
  }

  /**
   * Moves an attachment to a different folder
   * @param attachmentId - The ID of the attachment to move
   * @param targetFolderId - The target folder ID
   * @param delegationId - Optional delegation ID
   * @returns Observable of the move response
   */
  moveAttachment(
    attachmentId: number,
    targetFolderId: number,
    delegationId: number | null = null
  ): Observable<any> {
    return this.attachmentService.move(attachmentId, targetFolderId, delegationId);
  }

  /**
   * Validates if a filename already exists in a list of filenames
   * Case-insensitive comparison
   * @param fileName - The filename to validate
   * @param existingFileNames - Array of existing filenames
   * @param excludeFileName - Optional filename to exclude from validation
   * @returns true if filename already exists, false otherwise
   */
  fileNameExists(
    fileName: string,
    existingFileNames: string[],
    excludeFileName?: string
  ): boolean {
    return existingFileNames.some(
      (name) =>
        name.toLowerCase() === fileName.toLowerCase() &&
        (!excludeFileName || excludeFileName !== name)
    );
  }

  /**
   * Sanitizes filename to remove or replace invalid characters
   * @param fileName - The filename to sanitize
   * @returns Sanitized filename
   */
  sanitizeFileName(fileName: string): string {
    // Remove invalid characters for filenames
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/g;
    return fileName.replace(invalidChars, '_');
  }

  /**
   * Gets the file extension from a filename
   * @param fileName - The filename
   * @returns The file extension without dot or empty string
   */
  getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  /**
   * Gets the filename without extension
   * @param fileName - The filename
   * @returns The filename without extension
   */
  getFileNameWithoutExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex === -1 ? fileName : fileName.substring(0, lastDotIndex);
  }

  /**
   * Gets appropriate icon name based on file MIME type
   * @param mimeType - The MIME type of the file
   * @returns Material icon name
   */
  getFileIcon(mimeType: string): string {
    if (!mimeType) return 'insert_drive_file';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'videocam';
    if (mimeType.startsWith('audio/')) return 'audiotrack';
    if (mimeType.includes('pdf')) return 'picture_as_pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'table_chart';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'slideshow';
    if (
      mimeType.includes('zip') ||
      mimeType.includes('rar') ||
      mimeType.includes('compressed')
    )
      return 'folder_zip';
    if (mimeType.includes('text/')) return 'text_snippet';
    return 'insert_drive_file';
  }

  /**
   * Formats file size for display
   * @param bytes - The size in bytes
   * @returns Formatted size string (e.g., "1.5 MB")
   */
  formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}
